import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';

type Item = { to: string; label: string; icon: string };
type Group = { key: string; label: string; icon: string; items: Item[] };

const GROUPS: Group[] = [
  {
    key: 'email',
    label: 'E-mail',
    icon: '📧',
    items: [
      { to: '/gmail', label: 'Contas Gmail', icon: '📧' },
      { to: '/regras', label: 'Regras', icon: '⚙️' },
      { to: '/templates', label: 'Templates', icon: '📄' },
    ],
  },
  {
    key: 'fiscal',
    label: 'Fiscal',
    icon: '🧾',
    items: [
      { to: '/nfse', label: 'NFS-e', icon: '🧾' },
      { to: '/empresa', label: 'Empresa', icon: '🏢' },
    ],
  },
  {
    key: 'notif',
    label: 'Notificações',
    icon: '🔔',
    items: [{ to: '/notificacoes', label: 'Notificações', icon: '🔔' }],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState<string | null>(null); // qual menu/dropdown está aberto

  const close = () => setOpen(null);
  const groupActive = (g: Group) => g.items.some((i) => location.pathname === i.to);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-brand-600 text-white shadow sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-y-2">
          <Link to="/" className="font-bold hover:opacity-80 flex items-center gap-2" onClick={close}>
            <img src="/favicon.png" alt="" className="w-6 h-6 rounded bg-white/90 p-0.5" />
            Maestro
          </Link>

          <nav className="order-3 sm:order-2 w-full sm:w-auto flex flex-wrap justify-center gap-1 sm:gap-2 text-sm">
            {GROUPS.map((g) => {
              // Grupo com 1 item vira link direto (sem dropdown).
              if (g.items.length === 1) {
                const it = g.items[0];
                return (
                  <NavLink
                    key={g.key}
                    to={it.to}
                    onClick={close}
                    className={({ isActive }) =>
                      `px-2.5 py-1.5 rounded whitespace-nowrap flex items-center gap-1 ${isActive ? 'bg-white/20 font-bold' : 'hover:bg-white/10'}`
                    }
                  >
                    <span aria-hidden>{g.icon}</span>
                    {g.label}
                  </NavLink>
                );
              }
              return (
                <div key={g.key} className="relative">
                  <button
                    type="button"
                    onClick={() => setOpen(open === g.key ? null : g.key)}
                    className={`px-2.5 py-1.5 rounded whitespace-nowrap flex items-center gap-1 ${groupActive(g) ? 'bg-white/20 font-bold' : 'hover:bg-white/10'}`}
                  >
                    <span aria-hidden>{g.icon}</span>
                    {g.label}
                    <span className="text-[10px] opacity-70">▾</span>
                  </button>
                  {open === g.key && (
                    <div className="absolute left-0 mt-1 min-w-44 bg-white text-slate-800 rounded-lg shadow-lg py-1 z-30">
                      {g.items.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          onClick={close}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 ${isActive ? 'font-semibold text-brand-700' : ''}`
                          }
                        >
                          <span aria-hidden>{it.icon}</span>
                          {it.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Perfil */}
          <div className="order-2 sm:order-3 relative">
            <button
              type="button"
              onClick={() => setOpen(open === 'profile' ? null : 'profile')}
              aria-label="Perfil"
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
            >
              <PersonIcon />
            </button>
            {open === 'profile' && (
              <div className="absolute right-0 mt-1 min-w-56 bg-white text-slate-800 rounded-lg shadow-lg p-3 z-30 space-y-2">
                <div className="text-xs text-slate-500">Conectado como</div>
                <div className="text-sm font-medium break-words">{user?.email}</div>
                {user?.name && <div className="text-xs text-slate-500">{user.name}</div>}
                <button
                  onClick={() => {
                    close();
                    logout();
                    navigate('/login');
                  }}
                  className="w-full text-sm px-3 py-2 rounded bg-slate-100 hover:bg-slate-200 text-left"
                >
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Overlay pra fechar dropdowns ao clicar fora */}
      {open && <div className="fixed inset-0 z-10" onClick={close} aria-hidden />}

      <main className="max-w-4xl mx-auto w-full px-4 py-6 flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function PersonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
